import { createFocusTrap } from '@theme/utils';

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.handleCardClick = this.handleCardClick.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    this.addEventListener('click', this.handleCardClick);
    this.setupPopupFocusTrap();
    this.syncDecrementButtons();
  }

  cacheElements() {
    // Cache all quantity controls
    this.quantityInput = this.querySelector('[js-product-card="quantity"]');
    this.popupQuantityInput = this.querySelector('[js-product-card="popup-quantity"]');
  }

  setupPopupFocusTrap() {
    // Find the popup element (could be inside or outside this component)
    const popupId = this.querySelector('[popovertarget]')?.getAttribute('popovertarget');
    if (!popupId) return;

    const popup = document.getElementById(popupId);
    if (!popup) return;

    this.popupFocusTrap = createFocusTrap(popup);

    // Listen for popover toggle events
    popup.addEventListener('toggle', (event) => {
      const isOpen = popup.matches(':popover-open');

      if (isOpen) {
        this.popupFocusTrap.enable();
        this.syncQuantityInputs();

        // Focus the close button when popup opens
        const closeButton = popup.querySelector('.product-card-popup__close');
        if (closeButton) {
          setTimeout(() => {
            closeButton.focus();
          }, 0);
        }
      } else {
        this.popupFocusTrap.disable();
      }
    });

    popup.addEventListener('click', (event) => {
      if (event.target === popup) {
        popup.hidePopover();
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleCardClick);

    if (this.popupFocusTrap) {
      this.popupFocusTrap.disable();
    }
  }

  handleCardClick(event) {
    const actionElement = event.target.closest('[js-product-card]');

    if (!actionElement || !this.contains(actionElement)) {
      return;
    }

    const action = actionElement.getAttribute('js-product-card');
    const handledActions = ['increment', 'decrement', 'popup-increment', 'popup-decrement', 'popup-add'];

    if (!handledActions.includes(action)) {
      return;
    }

    this.handleQuantityChange(action);
  }

  handleQuantityChange(action) {
    this.cacheElements();

    if (!this.quantityInput) return;

    const currentValue = parseInt(this.quantityInput.value) || 0;
    let newValue = currentValue;

    switch (action) {
      case 'increment':
      case 'popup-increment':
        newValue = currentValue + 1;
        break;
      case 'decrement':
      case 'popup-decrement':
        newValue = Math.max(0, currentValue - 1);
        break;
      case 'popup-add':
        newValue = Math.max(1, currentValue);
        break;
      default:
        return;
    }

    // Update both inputs synchronously
    this.quantityInput.value = newValue;

    if (this.popupQuantityInput) {
      this.popupQuantityInput.value = newValue;
    }

    this.syncDecrementButtons(newValue);

    // Dispatch custom event
    if (newValue !== currentValue) {
      this.dispatchEvent(new CustomEvent('productQuantityChanged', {
        detail: { productId: this.quantityInput.dataset.productId, quantity: newValue, productCard: this },
        bubbles: true
      }));
    }
  }

  syncQuantityInputs() {
    this.cacheElements();

    if (!this.quantityInput) {
      return;
    }

    const quantity = Number.parseInt(this.quantityInput.value, 10) || 0;

    if (this.popupQuantityInput) {
      this.popupQuantityInput.value = quantity;
    }

    this.syncDecrementButtons(quantity);
  }

  syncDecrementButtons(quantity = Number.parseInt(this.quantityInput?.value, 10) || 0) {
    const shouldDisable = quantity <= 0;
    this.querySelectorAll('[js-product-card="decrement"], [js-product-card="popup-decrement"]').forEach((button) => {
      button.disabled = shouldDisable;
    });
  }
}

customElements.define('product-card', ProductCard);
